import axios from "axios";
import * as config from "./api/config";
import { decryptPayload, getDeviceId, initDeviceSession } from "../utils/deviceSecurity";

let _cachedToken: string | null | undefined = undefined;
let isRefreshingToken = false;
let refreshTokenPromise: Promise<string | null> | null = null;
let lastFailedRequest: (() => Promise<any>) | null = null;

const authErrorListeners = new Set<() => void>();
const sessionRefreshedListeners = new Set<() => void>();

export const addAuthErrorListener = (listener: () => void) => {
  authErrorListeners.add(listener);
  return () => { authErrorListeners.delete(listener); };
};

export const notifyAuthError = () => {
  if (process.env.NODE_ENV === 'development') {
    console.log("📢 Triggering notifyAuthError -> Opening AuthErrorModal");
  }
  authErrorListeners.forEach(fn => fn());
};

export const addSessionRefreshedListener = (listener: () => void) => {
  sessionRefreshedListeners.add(listener);
  return () => { sessionRefreshedListeners.delete(listener); };
};

export const notifySessionRefreshed = () => {
  sessionRefreshedListeners.forEach(fn => fn());
};

export const getToken = async (): Promise<string | null> => {
  if (_cachedToken !== undefined && _cachedToken !== null) return _cachedToken;
  _cachedToken = localStorage.getItem('t');
  return _cachedToken;
};

export const updateToken = async (newToken: string | null) => {
  _cachedToken = newToken;
  if (newToken) {
    localStorage.setItem('t', newToken);
  } else {
    localStorage.removeItem('t');
  }
};

export const clearTokenCache = () => {
  _cachedToken = undefined;
};

export const refreshTokenFlow = async (): Promise<string | null> => {
  if (isRefreshingToken && refreshTokenPromise) {
    return refreshTokenPromise;
  }
  isRefreshingToken = true;
  refreshTokenPromise = (async () => {
    try {
      const deviceId = getDeviceId();
      const appName = 'SnaptoonWeb';
      await initDeviceSession(appName);
      const { getT } = require('./api/auth');
      const response = await getT(deviceId, appName);
      const newToken = response?.data?.data || response?.data;
      if (newToken && typeof newToken === 'string') {
        await updateToken(newToken);
        return newToken;
      }
      return null;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.log('Refresh token flow error:', error);
      return null;
    } finally {
      isRefreshingToken = false;
      refreshTokenPromise = null;
    }
  })();
  return refreshTokenPromise;
};

export const retryLastFailedRequest = async () => {
  if (lastFailedRequest) {
    const retryFn = lastFailedRequest;
    lastFailedRequest = null;
    return await retryFn();
  }
};

async function processResponse(response: any) {
  if (response && response.data && typeof response.data === 'object' && response.data.payload) {
    try {
      const decrypted = await decryptPayload(response.data.payload);
      response.data = decrypted;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Central response payload decryption failed:', error);
      }
    }
  }
  return response;
}

const getHttpStatus = (error: any): number | undefined => {
  return error?.response?.status || error?.status || error?.statusCode || error?.response?.data?.status || error?.response?.data?.statusCode;
};

const executeRequest = async (
  method: 'get' | 'post' | 'put' | 'delete',
  path: string,
  payload?: any,
  configOptions: any = {},
  isRetry: boolean = false
): Promise<any> => {
  const token = await getToken();
  const headers = {
    "Content-Type": "application/json",
    "ngrok-skip-browser-warning": "69420",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(configOptions?.headers || {})
  };
  const Url = config.API_URL + path;
  if (process.env.NODE_ENV === 'development') console.log(`[HTTP ${method.toUpperCase()}]`, Url);

  try {
    let response;
    if (method === 'get') {
      response = await axios.get(Url, { headers, ...configOptions });
    } else if (method === 'post') {
      response = await axios.post(Url, payload, { headers, ...configOptions });
    } else if (method === 'put') {
      response = await axios.put(Url, payload, { headers, ...configOptions });
    } else if (method === 'delete') {
      response = await axios.delete(Url, { headers, data: payload, ...configOptions });
    }
    return await processResponse(response);
  } catch (error: any) {
    const status = getHttpStatus(error);

    if (status === 401) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`🔒 401 Unauthorized encountered on ${path} (isRetry: ${isRetry}, skipAuthRetry: ${!!configOptions?.skipAuthRetry})`);
      }

      if (!isRetry && !configOptions?.skipAuthRetry) {
        lastFailedRequest = () => executeRequest(method, path, payload, configOptions, true);

        const newToken = await refreshTokenFlow();
        if (newToken) {
          try {
            return await executeRequest(method, path, payload, configOptions, true);
          } catch (retryErr: any) {
            const retryStatus = getHttpStatus(retryErr);
            if (retryStatus === 401) {
              notifyAuthError();
            }
            throw retryErr;
          }
        } else {
          notifyAuthError();
        }
      } else {
        notifyAuthError();
      }
    }

    if (error.response) {
      throw error.response;
    }
    throw error;
  }
};

export const GET = (path: string, configOptions?: any) => executeRequest('get', path, undefined, configOptions);
export const POST = (path: string, payload?: any, configOptions?: any) => executeRequest('post', path, payload, configOptions);
export const PUT = (path: string, payload?: any, configOptions?: any) => executeRequest('put', path, payload, configOptions);
export const DELETE = (path: string, payload?: any, configOptions?: any) => executeRequest('delete', path, payload, configOptions);